import { Tooltip } from "bootstrap";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  useCreateFreeSubscriptionPaymentMutation,
  useCreateSubscriptionPaymentMutation,
  useGetPaymentPlanQuery,
} from "../../store/api/admin/paymentSlice";
import { updateLogInUser } from "../../store/reducer/UserReducer";
import PlanSkeleton from "./PlanSkeleton";
import "./plan.scss";

const PricingPlan = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.UserReducer);
  const [plans, setPlans] = useState([]);
  const [id, setId] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [currencyError, setCurrencyError] = useState("");
  const [cardsOpacity, setCardsOpacity] = useState(1);

  // Get PAYMENT & PLAN
  const { data, isSuccess, isLoading } = useGetPaymentPlanQuery({});

  // Subscription
  const [createSubscriptionPayment, { isLoading: isCreateLoading }] =
    useCreateSubscriptionPaymentMutation();

  // Create Free Subscription
  const [createFreeSubscriptionPayment, { isLoading: isCreateFreeLoading }] =
    useCreateFreeSubscriptionPaymentMutation();

  // Effect to update data and pagination
  useEffect(() => {
    if (isSuccess && data?.data) {
      setPlans(data?.data?.plans);
    }
  }, [data, isSuccess]);

  const handleCurrencyChange = (e) => {
    setSelectedCurrency(e.target.value);
  };

  const handleSubmit = (planId) => {
    toast.dismiss();
    setId(planId);
    setSelectedPlanId(planId);
    setShowModal(true);
    setCardsOpacity(0.2);
  };

  const handleSubmitFree = async (planId) => {
    toast.dismiss();
    setId(planId);

    try {
      const response = await createFreeSubscriptionPayment({
        planId: planId,
      }).unwrap();

      dispatch(
        updateLogInUser({
          plan_id: response.data.plan_id,
          is_payment_active: 1,
          payment_status: "Completed",
        }),
      );

      toast.success(
        response.message || "Free subscription activated successfully!",
      );
    } catch (err) {
      const message =
        err?.data?.message || err.message || "Something went wrong!";
      toast.error(message);
    } finally {
      setId("");
    }
  };

  const handleNext = async () => {
    if (!selectedCurrency) {
      setCurrencyError("Please select a currency.");
      return;
    } else {
      setCurrencyError("");
    }

    if (selectedPlanId) {
      try {
        let response = await createSubscriptionPayment({
          planId: selectedPlanId,
          currency: selectedCurrency,
        }).unwrap();
        window.open(response.data.iframe_url, "_blank");
        setShowModal(false);
        setCardsOpacity(1);
      } catch (err) {
        const message =
          err?.data?.message || err.message || "Something went wrong!";
        toast.error(message);
      } finally {
        setSelectedPlanId(null);
      }
    }
  };

  useEffect(() => {
    const tooltipTriggerEl = document.querySelector(
      '[data-bs-toggle="tooltip"]',
    );
    if (tooltipTriggerEl) {
      new Tooltip(tooltipTriggerEl);
    }
  }, []);

  return (
    <>
      <div className="plans">
        <section
          id="pricing"
          className="pricing-content section-padding"
          style={{ opacity: cardsOpacity }}
        >
          <div className="container">
            <div className="row text-center justify-content-center">
              {isLoading ? (
                <PlanSkeleton count={6} />
              ) : plans.length > 0 ? (
                plans.map((plan, i) => (
                  <div
                    className="col-lg-3 col-md-6 col-xs-12 wow fadeInUp"
                    key={i}
                    data-wow-duration="1s"
                    data-wow-delay="0.1s"
                    data-wow-offset="0"
                  >
                    <div
                      key={plan.id}
                      className={`single-pricing ${
                        i % 2 !== 0 ? "single-pricing-white" : ""
                      }`}
                    >
                      <div className="price-head">
                        <h2>{plan.name}</h2>
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                      <h1 className="price">${plan.price_cents / 100}</h1>
                      <h5>{t("monthly")}</h5>
                      {/* Plan Limits Section */}
                      <div className="plan-limits my-3">
                        <div className="limit-item d-flex justify-content-between align-items-center px-3 py-2">
                          <span
                            className={`fw-medium  ${
                              i % 2 !== 0 ? "text-white" : ""
                            }`}
                          >
                            {t("max")} {t("seats")} ({t("riders")}):
                          </span>
                          <span className="badge bg-primary">
                            {plan.parent_max_limit}
                          </span>
                        </div>

                        <div className="limit-item d-flex justify-content-between align-items-center px-3 py-2">
                          <span
                            className={`fw-medium  ${
                              i % 2 !== 0 ? "text-white" : ""
                            }`}
                          >
                            {t("max")} {t("vehicles")}:
                          </span>
                          <span className="badge bg-primary">
                            {plan.vehicle_max_limit}
                          </span>
                        </div>
                      </div>
                      <ul className="p-0">
                        <li>{plan.description}</li>
                      </ul>
                      {user?.plan_id === plan.id &&
                      user?.is_payment_active === 1 ? (
                        <button
                          type="button"
                          className="btn btn-success border-0 bg-success subscribed-btn"
                          disabled
                          style={{ color: "lightgrey" }}
                        >
                          {t("btns.subscribed")}
                        </button>
                      ) : plan.price_cents === 0 ? (
                        <button
                          type="button"
                          onClick={() => handleSubmitFree(plan.id)}
                          disabled={
                            (isCreateFreeLoading && id === plan.id) ||
                            user.is_free_used === 0
                          }
                          className="free-plan-btn"
                        >
                          {isCreateFreeLoading && id === plan.id ? (
                            <div
                              className="spinner-border loaderStyle2"
                              role="status"
                            >
                              <span className="sr-only p-0"></span>
                            </div>
                          ) : (
                            t("btns.subscribe")
                          )}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleSubmit(plan.id)}
                          disabled={isCreateLoading && id === plan.id}
                        >
                          {t("btns.subscribe")}
                        </button>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                `${t("no")} ${t("subscription")} ${t("plans")} ${t("found")}.`
              )}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        {/* Contact Section - Updated to match the image */}
        {!isLoading && (
          <div className="contact-footer py-4">
            <div className="container">
              <div className="contact-info-wrapper text-center mx-auto">
                <p className="mb-0 d-flex align-items-center justify-content-center flex-wrap gap-2">
                  <span className="fw-medium text-white">
                    {/* {vendor ? (
                      <>
                        {t("need")} {t("than")} 60 {t("seaats")}, {t("kindly")}{" "}
                        {t("contacts")} {t("us")}:
                      </>
                    ) : (
                      <>
                        {t("More")} {t("than")} 500 {t("seaats")}, {t("kindly")}{" "}
                        {t("contacts")} {t("us")}:
                      </>
                    )} */}
                    {t("need")} {t("more1")} {t("seaats")}, {t("kindly")}{" "}
                    {t("contacts")} {t("us")}:
                  </span>
                  <a
                    href="tel:+16137165770"
                    className="text-decoration-none mx-2"
                  >
                    +1 (613) 716-5770
                  </a>
                  <span className="text-muted">|</span>
                  <a
                    href="mailto:Sayhello@Aeon-App.com"
                    className="text-decoration-none mx-2"
                  >
                    Sayhello@Aeon-App.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Currency Selection Modal */}
      {showModal && (
        <div
          className="modal fade show"
          id="currencyModal"
          tabIndex="-1"
          role="dialog"
          aria-labelledby="currencyModalLabel"
          aria-hidden="false"
          style={{ display: "block", zIndex: 1000 }}
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="currencyModalLabel">
                  Select Your Currency
                </h5>
                <button
                  type="button"
                  className="close"
                  data-dismiss="modal"
                  aria-label="Close"
                  onClick={() => {
                    setShowModal(false);
                    setCardsOpacity(1);
                  }}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <form>
                  <div className="form-group">
                    <label htmlFor="currencySelect" className="fs-6 mb-2">
                      Choose Currency
                    </label>
                    <select
                      className="form-control"
                      id="currencySelect"
                      value={selectedCurrency}
                      onChange={handleCurrencyChange}
                    >
                      <option value="" className="text-gray" disabled>
                        Select
                      </option>
                      <option value="USD">USD</option>
                      <option value="CAD">CAD</option>
                    </select>
                    {currencyError && (
                      <div className="text-danger pt-1">{currencyError}</div>
                    )}
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setCardsOpacity(1);
                  }}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleNext}
                >
                  {isCreateLoading ? (
                    <div className="spinner-border loaderStyle2" role="status">
                      <span className="sr-only p-0"></span>
                    </div>
                  ) : (
                    "Next"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PricingPlan;
